import { useState, useRef, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { Textarea, Text, Stack, Loader, ScrollArea, ActionIcon, Avatar, NavLink, Group, Button } from '@mantine/core';
import { IconArrowRight, IconMessagePlus, IconTrash, IconSparkles } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api-service';
import { ChatMessage } from '@turizi/shared';
import { setWebTitle } from '@/lib/format-utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import logoUrl from '/images/favicon.svg';


export default function AiChatPage() {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [sessions, setSessions] = useState<{ id: string; title: string, updated_at: string }[]>([]);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const scrollViewport = useRef<HTMLDivElement>(null);
    const isInitialLoad = useRef(true);

    setWebTitle(t('ai-chat'));

    const scrollToBottom = (instant = false) => {
        if (scrollViewport.current) {
            scrollViewport.current.scrollTo({
                top: scrollViewport.current.scrollHeight,
                behavior: instant ? 'instant' : 'smooth'
            });
        }
    };

    // Scroll to bottom when new messages arrive (smooth) or when loading indicator appears
    useEffect(() => {
        if (!isInitialLoad.current) {
            scrollToBottom(false);
        }
    }, [messages, isLoading]);

    // Load list of sessions on mount
    const fetchSessions = async () => {
        try {
            const response = await api.aiAgent.getSessions();
            if (response.data) {
                setSessions(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch sessions:', error);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, []);

    // Load active session history when currentSessionId changes
    useEffect(() => {
        const fetchHistory = async () => {
            if (!currentSessionId) {
                setMessages([]);
                setIsLoadingHistory(false);
                return;
            }

            setIsLoadingHistory(true);
            try {
                const response = await api.aiAgent.getHistory(currentSessionId);
                if (response.data && response.data.messages) {
                    setMessages(response.data.messages);
                }
            } catch (error) {
                console.error('Failed to fetch chat history:', error);
            } finally {
                setIsLoadingHistory(false);
                setTimeout(() => {
                    scrollToBottom(true);
                    isInitialLoad.current = false;
                }, 0);
            }
        };

        fetchHistory();
    }, [currentSessionId]);

    const handleSendMessage = async (msg: string) => {
        if (!msg.trim() || isLoading) return;

        const userMessage: ChatMessage = {
            role: 'user',
            content: msg.trim(),
        };

        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setIsLoading(true);

        // scroll to bottom
        setTimeout(() => {
            scrollToBottom(false);
        }, 0);

        try {
            const response = await api.aiAgent.chat({
                message: userMessage.content,
                sessionId: currentSessionId || undefined
            });

            if (response.data) {
                if (response.data.sessionId && response.data.sessionId !== currentSessionId) {
                    setCurrentSessionId(response.data.sessionId);
                    fetchSessions(); // Refresh sidebar
                }
                const assistantMessage: ChatMessage = {
                    role: 'assistant',
                    content: response.data.message || '',
                };
                setMessages([...updatedMessages, assistantMessage]);
            } else if (response.error) {
                const errorMessage: ChatMessage = {
                    role: 'assistant',
                    content: `Error: ${typeof response.error === 'string' ? response.error : response.error.message || 'Unknown error'}`,
                };
                setMessages([...updatedMessages, errorMessage]);
            }
        } catch (error: any) {
            const errorMessage: ChatMessage = {
                role: 'assistant',
                content: `Error: ${error.message || 'Failed to send message'}`,
            };
            setMessages([...updatedMessages, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex gap-1 items-start justify-between p-2 h-[calc(100vh-100px)] w-full">

            {/* Main Chat Area */}
            <div className="flex flex-col flex-1 relative h-full overflow-hidden">
                {!isLoadingHistory && messages.length === 0 ? (
                    <div className="flex flex-col h-full justify-center items-center p-4">
                        <div className="flex flex-col items-center justify-center w-full max-w-2xl space-y-6">
                            <div className="flex gap-2 flex-col text-gray-300 justify-center items-center mb-4">
                                <img src={logoUrl} className="w-8 opacity-25" alt='' />
                                <Text size="lg" fw={500} c="dimmed">{t('ai-chat')}</Text>
                            </div>
                            <div className="w-full">
                                <ChatInput onSendMessage={handleSendMessage} isLoading={isLoading} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col h-full w-full max-w-3xl mx-auto min-h-0">
                        <ScrollArea
                            viewportRef={scrollViewport}
                            className="flex-1 px-8"
                        >
                            {isLoadingHistory ? (
                                <div className="flex justify-center items-center h-full py-8">
                                    <Loader size="md" color="black" type="dots" />
                                </div>
                            ) : (
                                <Stack gap="xl" className="pb-4 max-w-3xl mx-auto w-full">
                                    {messages.map((message, index) => (
                                        <ChatMessageItem key={index} message={message} />
                                    ))}

                                    {isLoading && (
                                        <div className="flex justify-start">
                                            <Loader size="sm" color="gray" type="dots" />
                                        </div>
                                    )}
                                </Stack>
                            )}
                        </ScrollArea>
                        <div className="w-full px-4 pb-4 shrink-0">
                            <ChatInput onSendMessage={handleSendMessage} isLoading={isLoading} />
                        </div>
                    </div>
                )}
            </div>

            {/* Minimap / Sidebar */}
            <div className="w-64 flex flex-col">
                <div className="p-4 border-b border-gray-100">
                    <Button
                        fullWidth
                        variant="subtle"
                        color="black"
                        leftSection={<IconMessagePlus size={16} />}
                        onClick={() => setCurrentSessionId(null)}
                    >
                        {t('new_chat')}
                    </Button>
                </div>
                <ScrollArea className="">
                    {sessions.map((session) => (
                        <NavLink
                            key={session.id}
                            active={session.id === currentSessionId}
                            label={session.title || t('new_chat')}
                            onClick={() => setCurrentSessionId(session.id)}
                            variant="light"
                            className="rounded-md mb-1"
                            rightSection={
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color="red"
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        await api.aiAgent.deleteSession(session.id);
                                        if (session.id === currentSessionId) setCurrentSessionId(null);
                                        fetchSessions();
                                    }}
                                >
                                    <IconTrash size={14} />
                                </ActionIcon>
                            }
                        />
                    ))}
                </ScrollArea>
            </div>
        </div>
    );
}

const ChatMessageItem = memo(({ message }: { message: ChatMessage }) => {
    return (
        <div
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
            <div
                className={` 
                    ${message.role === 'user'
                        ? 'px-4 py-2 rounded-xl bg-gray-200 text-black'
                        : 'w-[100%]'
                    }`}
            >
                <div className={`
                    text-base leading-loose prose max-w-none 
                    overflow-x-scroll
                    ${message.role === 'user' ? 'prose-invert' : 'prose-gray'}
                `}>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            a: ({ node, ...props }) => {
                                if (props.href && props.href.startsWith('/')) {
                                    return <Link to={props.href} className="text-blue-600 hover:underline">{props.children}</Link>;
                                }
                                return <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" />;
                            }
                        }}
                    >
                        {message.content}
                    </ReactMarkdown>
                </div>
            </div>
        </div>
    );
});

const ChatInput = ({ onSendMessage, isLoading }: { onSendMessage: (msg: string) => void, isLoading: boolean }) => {
    const { t } = useTranslation();
    const [inputMessage, setInputMessage] = useState('');

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (inputMessage.trim() && !isLoading) {
                onSendMessage(inputMessage.trim());
                setInputMessage('');
            }
        }
    };

    const handleSend = () => {
        if (inputMessage.trim() && !isLoading) {
            onSendMessage(inputMessage.trim());
            setInputMessage('');
        }
    };

    return (
        <div className="flex items-center gap-2 bg-white border border-gray-300 shadow-md rounded-xl px-2 py-2">
            <Textarea
                size="md"
                placeholder={t('ai-chat-placeholder')}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                minRows={3}
                maxRows={6}
                autosize
                styles={{
                    root: { flex: 1 },
                    input: { border: 'none', background: 'transparent' }
                }}
                disabled={isLoading}
            />
            <ActionIcon
                size="lg"
                radius="xl"
                variant="filled"
                color="#3ECF8E"
                onClick={handleSend}
                disabled={!inputMessage.trim() || isLoading}
                className="transition-transform hover:scale-105"
            >
                <IconArrowRight size={18} />
            </ActionIcon>
        </div>
    );
};
